import { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import { HighlightCard } from '@/components/HighlightCard';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useEngagement } from '@/hooks/useEngagement';
import { useHighlightSources } from '@/hooks/useHighlightSources';
import { useMyDeletions } from '@/hooks/useMyDeletions';
import type { Highlight } from '@/lib/highlights';

export function HighlightCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-4 sm:p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Skeleton className="size-8 rounded-full" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-3 w-12" />
        </div>
        <div className="border-l-4 border-primary/30 pl-4 space-y-2">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-4/5" />
          <Skeleton className="h-5 w-3/5" />
        </div>
        <Skeleton className="h-[4.5rem] w-full rounded-lg" />
      </CardContent>
    </Card>
  );
}

interface HighlightListProps {
  highlights: Highlight[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}

/** A list of highlight cards with batched source resolution and optional infinite scroll. */
export function HighlightList({ highlights, hasNextPage, isFetchingNextPage, fetchNextPage }: HighlightListProps) {
  const sources = useHighlightSources(highlights);
  const deletions = useMyDeletions();

  // Hide highlights the viewer has deleted, even if a relay still serves them.
  const visible = useMemo(() => {
    const deleted = deletions.data;
    if (!deleted || deleted.size === 0) return highlights;
    return highlights.filter((h) => !deleted.has(h.event.id));
  }, [highlights, deletions.data]);

  const engagement = useEngagement(visible);

  const { ref, inView } = useInView({ rootMargin: '600px' });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && fetchNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="space-y-4">
      {visible.map((highlight) => (
        <HighlightCard
          key={highlight.event.id}
          highlight={highlight}
          sources={sources.data}
          sourcesLoading={sources.isPending}
          stats={engagement.data?.get(highlight.event.id)}
        />
      ))}

      {hasNextPage && (
        <div ref={ref} className="pt-2">
          {isFetchingNextPage && <HighlightCardSkeleton />}
        </div>
      )}
    </div>
  );
}
